import React from "react";

const wrapWithRequired = (validator, isRequired) => {
	if (isRequired) {
		return {
			isRequired: validator,
		};
	}

	return validator;
};

const validClassNamesObjectPrefix = (prefix, isRequired) =>
	wrapWithRequired((props, propName, componentName) => {
		const obj = props?.[propName];
		if (!obj) {
			if (isRequired) {
				return new Error(`"${propName}" is required for "${componentName}".`);
			}
			return null;
		}

		const keys = Object.keys(obj);
		if (keys.length === 0) {
			return new Error(`Invalid prop "${propName}" supplied to "${componentName}".`);
		}

		let prefixRegex;

		if (Array.isArray(prefix)) {
			prefixRegex = prefix.map(regExp => new RegExp(`^${regExp}`));
		} else {
			prefixRegex = [new RegExp(`^${prefix}`)];
		}

		for (let i = 0; i < keys.length; i++) {
			const key = keys[i];
			const propFullNameWithKey = `${propName}.${key}`;

			if (!prefixRegex.some(regex => regex.test(key))) {
				return new Error(
					`Invalid key "${propFullNameWithKey}" supplied to "${componentName}". Key is expected to match "${prefix}".`
				);
			}
		}

		return null;
	}, isRequired);

const validReactComponentsByDisplayName = (displayName, isRequired) =>
	wrapWithRequired((props, propName, componentName) => {
		const children = props?.[propName];

		if (!children) {
			if (isRequired) {
				return new Error(`"${propName}" is required for "${componentName}".`);
			}
			return null;
		}

		try {
			// Render props children
			if (!React.isValidElement(children) && !React.Children.count(children)) {
				const component = children();

				if (React.isValidElement(component)) {
					if (component?.type?.displayName !== displayName) {
						return new Error(
							`Invalid "${propName}" supplied to "${componentName}". "${propName}" is expected to be a "${displayName}" component but received "${component?.type?.displayName}".`
						);
					}

					return null;
				}

				if (React.Children.count(component)) {
					for (let index = 0; index < React.Children.count(component); index++) {
						const element = component[index];

						if (element?.type?.displayName !== displayName) {
							return new Error(
								`Invalid "${propName}" supplied to "${componentName}". "${propName}" is expected to be a "${displayName}" component but received "${element?.type?.displayName}".`
							);
						}
					}

					return null;
				}
			}

			if (React.isValidElement(children)) {
				if (children?.type?.displayName !== displayName) {
					return new Error(
						`Invalid "${propName}" supplied to "${componentName}". "${propName}" is expected to be a "${displayName}" component but received "${children?.type?.displayName}".`
					);
				}

				return null;
			}

			if (React.Children.count(children)) {
				for (let index = 0; index < React.Children.count(children); index++) {
					const element = children[index];

					if (element?.type?.displayName !== displayName) {
						return new Error(
							`Invalid "${propName}" supplied to "${componentName}". "${propName}" is expected to be a "${displayName}" component but received "${element?.type?.displayName}".`
						);
					}
				}

				return null;
			}
		} catch (err) {
			return new Error(err.message);
		}

		return new Error(
			`Invalid "${propName}" supplied to "${componentName}". "${propName}" is expected to be a "${displayName}" component.`
		);
	}, isRequired);

export { validClassNamesObjectPrefix, validReactComponentsByDisplayName };
